import { useEffect } from 'react';

import UserIcon from '@/assets/user.svg?react';

import BoltIcon from '@/assets/bolt.svg?react';
import CloseIcon from '@/assets/close-line.svg?react';
import LayerIcon from '@/assets/layer.svg?react';
import PencilIcon from '@/assets/pencil.svg?react';
import Button from '@/components/ui/Button';
import Modal, {
  ModalClose,
  ModalContent,
  ModalTitle,
} from '@/components/ui/Modal';
import Select from '@/components/ui/Select/Select';

import {
  type EcommerceFormValues,
  LINE_TYPE,
  type LineType,
  PLOT_TYPE,
  type PlotType,
} from '../../schema';
import SectionHeader from '../SectionHeader';
import PlotOptionCard from './PlotOptionCard';
import { useLingui } from '@lingui/react';
import { Controller, useForm } from 'react-hook-form';

type PlotValues = Pick<
  EcommerceFormValues,
  'line_types' | 'plot_type' | 'co_owners_count'
>;

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (values: PlotValues) => void;
  currentValues: PlotValues;
}

const CO_OWNERS_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const EditPlotInfoModal = ({ open, onClose, onSave, currentValues }: Props) => {
  const { i18n } = useLingui();

  const { control, handleSubmit, watch, reset, trigger } = useForm<PlotValues>({
    defaultValues: {
      line_types: currentValues.line_types ?? [],
      plot_type: currentValues.plot_type,
      co_owners_count: currentValues.co_owners_count,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        line_types: currentValues.line_types ?? [],
        plot_type: currentValues.plot_type,
        co_owners_count: currentValues.co_owners_count,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lineTypes = watch('line_types');
  const plotType = watch('plot_type');

  const lineTypeOptions: { value: LineType; label: string }[] = [
    { value: LINE_TYPE.LOW, label: i18n.t('Niskie napięcie') },
    { value: LINE_TYPE.MEDIUM, label: i18n.t('Średnie napięcie') },
    { value: LINE_TYPE.HIGH, label: i18n.t('Wysokie napięcie') },
    { value: LINE_TYPE.TRANSMISSION, label: i18n.t('Linia przesyłowa') },
    { value: LINE_TYPE.TRANSFORMER, label: i18n.t('Słup z transformatorem') },
  ];

  const plotTypeOptions: { value: PlotType; label: string }[] = [
    { value: PLOT_TYPE.AGRICULTURAL, label: i18n.t('Rolna') },
    {
      value: PLOT_TYPE.RESIDENTIAL_BUILDING,
      label: i18n.t('Budowlana mieszkaniowa'),
    },
    { value: PLOT_TYPE.SERVICE_BUILDING, label: i18n.t('Budowlana usługowa') },
    { value: PLOT_TYPE.INDUSTRIAL, label: i18n.t('Przemysłowa') },
    { value: PLOT_TYPE.FOREST, label: i18n.t('Leśna') },
    { value: PLOT_TYPE.NO_MPZP, label: i18n.t('Brak MPZP') },
    { value: PLOT_TYPE.UNKNOWN, label: i18n.t('Nie wiem') },
  ];

  const onSubmit = (values: PlotValues) => {
    onSave(values);
    onClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <ModalContent className="max-w-[620px] gap-0 overflow-hidden rounded-[20px] p-0 lg:p-0">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-dark-gray-100 px-6 py-4">
          <ModalTitle asChild>
            <h3 className="h3 flex items-center gap-2 text-blue-600">
              <PencilIcon width={20} className="shrink-0" />
              {i18n.t('Edytuj informacje o działce')}
            </h3>
          </ModalTitle>
          <ModalClose asChild>
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-200 [&>svg>path]:fill-dark-gray-300 hover:[&>svg>path]:fill-oxford-blue-500"
            >
              <CloseIcon width={16} />
            </button>
          </ModalClose>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5 md:gap-4">
            {/* Q1: Line types */}
            <div>
              <SectionHeader
                icon={BoltIcon}
                label={i18n.t('Jaki rodzaj linii znajduje się na działce?')}
                className="mb-3"
              />
              <Controller
                control={control}
                name="line_types"
                rules={{
                  validate: (value) =>
                    (value as LineType[]).length > 0 ||
                    i18n.t('Wybierz co najmniej jeden rodzaj linii'),
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  const selected = value as LineType[];
                  const toggle = (v: LineType) => {
                    if (selected.includes(v)) {
                      onChange(selected.filter((x) => x !== v));
                    } else {
                      onChange([...selected, v]);
                    }
                    trigger('line_types');
                  };
                  return (
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {lineTypeOptions.map((opt) => (
                          <PlotOptionCard
                            key={opt.value}
                            label={opt.label}
                            checked={selected.includes(opt.value)}
                            type="checkbox"
                            onClick={() => toggle(opt.value)}
                          />
                        ))}
                      </div>
                      {error && (
                        <p className="caption-large text-red-400">
                          {error.message}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>

            {/* Q2: Plot type */}
            {(lineTypes ?? []).length > 0 && (
              <div>
                <SectionHeader
                  icon={LayerIcon}
                  label={i18n.t('Jaki to rodzaj działki?')}
                  className="mb-3"
                />
                <Controller
                  control={control}
                  name="plot_type"
                  rules={{
                    validate: (value) =>
                      !!value || i18n.t('Wybierz typ działki'),
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {plotTypeOptions.map((opt) => (
                          <PlotOptionCard
                            key={opt.value}
                            label={opt.label}
                            checked={value === opt.value}
                            type="radio"
                            onClick={() => {
                              onChange(opt.value);
                              trigger('plot_type');
                            }}
                          />
                        ))}
                      </div>
                      {error && (
                        <p className="caption-large text-red-400">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Q3: Co-owners */}
            {!!plotType && (
              <div>
                <SectionHeader
                  icon={UserIcon}
                  label={i18n.t('Ilu jest współwłaścicieli działki?')}
                  className="mb-3"
                />
                <Controller
                  control={control}
                  name="co_owners_count"
                  rules={{
                    validate: (value) =>
                      !!value || i18n.t('Podaj liczbę współwłaścicieli'),
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div className="flex flex-col gap-2">
                      <Select
                        placeholder={i18n.t(
                          'Wskaż liczbę współwłaścicieli działki'
                        )}
                        value={value}
                        onChange={(v: string) => {
                          onChange(v);
                          trigger('co_owners_count');
                        }}
                        options={CO_OWNERS_OPTIONS}
                        error={error?.message}
                        success={!error && !!value}
                        className="caption-large"
                      />
                      {error && (
                        <p className="caption-large text-red-400">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-dark-gray-100 bg-gray-200 p-6">
            <Button
              size="small"
              type="button"
              intent="secondary"
              className="hover:bg-gray-400 hover:text-blue-600"
              onClick={onClose}
            >
              {i18n.t('Anuluj')}
            </Button>
            <Button size="small" type="submit">
              {i18n.t('Zapisz zmiany')}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EditPlotInfoModal;
